import apiClient from '../clients/apiClient';
import { Schemas } from '../types/api-types';

export type PlAccountResponse = Schemas['PlAccountResponse'];

export const getPlAccount = async (plAccountId: number): Promise<PlAccountResponse | null> => {
    try {
        const response = await apiClient.get(`/loan-application/pl_account/${plAccountId}`);
        return response.data.data || null;
    } catch (error) {
        return null;
    }
};

/** Returns the printed flag (0 = never printed, 1 = has been printed before). */
export const getPlAccountPrintStatus = async (plAccountId: number): Promise<{ printed: number } | null> => {
    try {
        const response = await apiClient.get(`/loan-application/pl_account/${plAccountId}/print-status`);
        return response.data.data || null;
    } catch {
        return null;
    }
};

/** Marks the account mandate as printed (sets printed=1 on pl_account). */
export const markPlAccountPrinted = async (plAccountId: number): Promise<void> => {
    await apiClient.post(`/loan-application/pl_account/${plAccountId}/mark-printed`);
};

/** Resets the passbook page and line numbers to 1 and 0. */
export const resetPassbookTracking = async (plAccountId: number): Promise<void> => {
    await apiClient.post(`/transaction/pl_account/${plAccountId}/reset-passbook`);
};

export const updatePlAccountInterest = async (pl_account_id: number, interest: number): Promise<{ interest: number } | null> => {
    try {
        const response = await apiClient.put(`/loan-application/pl_account/${pl_account_id}/interest`, { interest });
        return response.data.data || null;
    } catch (error) {
        return null;
    }
};

export const updatePlAccountInsurance = async (pl_account_id: number, insurance: number, reason: string): Promise<{ insurance: number }> => {
    try {
        const response = await apiClient.put(`/loan-application/pl_account/${pl_account_id}/insurance`, { insurance, reason });
        return response.data.data ?? { insurance };
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data?.detail || 'Failed to update insurance');
        }
        throw new Error('network error, please contact support team');
    }
};

export const updatePlAccountPenalty = async (pl_account_id: number, penalty: number, reason: string): Promise<{ penalty: number }> => {
    try {
        const response = await apiClient.put(`/loan-application/pl_account/${pl_account_id}/penalty`, { penalty, reason });
        return response.data.data ?? { penalty };
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data?.detail || 'Failed to update penalty');
        }
        throw new Error('network error, please contact support team');
    }
};

export interface PlLoanFirstLastAmotData {
    first_capital_installment: number | null;
    first_interest_installment: number | null;
    first_total_installment: number | null;
    first_installment_date: string | null;
    last_capital_installment: number | null;
    last_interest_installment: number | null;
    last_total_installment: number | null;
    last_installment_date: string | null;
}

export const getPlLoanFirstLastAmortization = async (plAccountId: number): Promise<PlLoanFirstLastAmotData | null> => {
    try {
        const response = await apiClient.get(`/loan-application/first-last-amortization/${plAccountId}`);
        return response.data.data || null;
    } catch (error) {
        return null;
    }
};
